import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUserStore } from '../../store/userStore';
import { startRefreshTimer } from '../../api/api';
import { getSessionStore } from '../../store/sessionStore';
import { resolvePostLoginRoute, resolvePromptCount } from '../../utils/userProfile';
import { PageBusyLoader } from '../ui/PageBusyLoader';

/**
 * SignupComplete Component
 * Handles OAuth redirect from backend after successful signup
 * Backend redirects here with token in query params: /signup/complete?token=JWT
 */
const SignupComplete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);
  const setTokens = useUserStore((state) => state.setTokens);

  useEffect(() => {
    let toastShown = false;

    const handleSignupComplete = async () => {
      try {
        const token = searchParams.get('token');
        const refresh = searchParams.get('refreshToken');

        if (!token) {
          throw new Error('No authentication token received');
        }

        localStorage.setItem('accessToken', token);
        if (refresh) {
          localStorage.setItem('refreshToken', refresh);
        }

        setTokens({ accessToken: token, refreshToken: refresh });

        startRefreshTimer();

        const session = await getSessionStore().bootstrapSession({
          force: true,
        });
        const userData = session.user;
        if (userData) {
          setUser(userData as never);
        }
        const promptCount = resolvePromptCount(session.prompts ?? []);
        const userRecord = userData as Record<string, unknown> | null;

        if (!toastShown) {
          toast.success('Account created successfully!');
          toastShown = true;
        }

        navigate(resolvePostLoginRoute(userRecord, promptCount));
      } catch (err: any) {
        console.error('Signup complete handling error:', err);
        if (!toastShown) {
          toast.error(err.message || 'Failed to process signup');
          toastShown = true;
        }
        navigate('/');
      }
    };

    handleSignupComplete();
  }, [searchParams, navigate, setUser, setTokens]);

  return <PageBusyLoader pageName="signup" label="Setting up your account..." overlay />;
};

export default SignupComplete;
